import _defineProperty from "@babel/runtime/helpers/defineProperty";
// Type declarations are used at compile-time as 'headers' only (does NOT include the files)

import { ClassUtils } from "abweb/utils/ClassUtils";
import { ArrayUtils } from "./ArrayUtils";
export class ObjectUtils {
  constructor() {
    // static class, can't create an instance
  }

  /**
   * Get all propertyDescriptors of object including prototype chain
   *
   * NOTE: This can eventually be improved by using Object.getOwnPropertyDescriptors but
   * is currently not available within browsers such as Chrome 51.
   *
   * @param {Object} instance
   * @returns {string[] list of property descriptors}
   */
  static protoTypePropertyDescriptors(instance) {
    const propertyDescriptors = [];
    let proto = Object.getPrototypeOf(instance);
    while (proto) {
      Object.getOwnPropertyNames(proto).forEach(name => {
        if (name !== "constructor") {
          const desc = Object.getOwnPropertyDescriptor(proto, name);
          if (desc) {
            propertyDescriptors.push(name);
          }
        }
      });
      proto = Object.getPrototypeOf(proto);
    }
    return propertyDescriptors;
  }
  static propertyValues(thing, sep = "\n") {
    const propNameValues = Object.getOwnPropertyNames(thing).map(function (key) {
      // Tell TypeScript that indexing an Object with a string is OK.
      return key + ": " + thing[key];
    });
    return propNameValues.join(sep);
  }
  static hasFunction(thing, funcName) {
    return funcName in thing && typeof thing[funcName] === "function";
  }

  // Formerly had isNonNull(thing) and isUndefined(thing) functions
  // but better replaced by native js/ts expressions: ! !thing and !thing respectively...

  /**
   * Gets the type (class) of an instance of a class.
   * @param instance Instance of a class.
   * @return Class (aka Function) of the instance's class.
   */
  static getClass(instance) {
    return instance != null ? instance.__proto__.constructor : null;
  }

  /**
   * Convert a json {...} literal into a (strongly-typed) Map<string, ValueT>
   * @param json
   * @returns {Map<string, ValueT>}
   */
  static jsonToMap(json) {
    const map = new Map();
    for (const key in json) {
      if (json.hasOwnProperty(key)) {
        map.set(key, json[key]);
      }
    }
    return map;
  }

  /**
   * Indicates whether a value is non-null and defined
   *
   * @param value       The value to check
   * @return {boolean}  Flag indicating whether the value is non-null and defined
   */
  static exists(value) {
    return value !== undefined && value != null;
  }

  /**
   * Gets a named property of an object.  Can follow a subproperty chain if desired.
   *
   * @param object    the object whose property to get
   * @param properties  the property chain to follow.  This is an array of property names, where each is a property name
   *                    of the prior level object.  It can be specified in three ways:
   *                    <ol>
   *                      <li>one name per parameter: <code>getProperty(object, name1, name2, name3, ...)</code></li>
   *                      <li>an array: <code>getProperty(object, [ name1, name2, name3, ...])</code></li>
   *                      <li>A combination of the two:
   *                          <code>getProperty(object, name 1, name 2, [ name3, name4, name5], name6)</code</li>
   *                    </ol>
   *                    Why so flexible?  Why not!  Knock yourself out.
   * @return {any}    The value of the requested property.
   */
  static getProperty(object, ...properties) {
    const props = ArrayUtils.flatten(properties);
    let value = object;
    for (let i = 0; i < props.length; i++) {
      const prop = props[i];
      if (prop in value) {
        value = value[prop];
      } else {
        return ObjectUtils.INVALID_PROPERTY_INDICATOR;
      }
    }
    return value;
  }

  /**
   * Gets names properties of an object, returning a typed Partial object containing those properties. The difference
   * between this and <code>{@link ObjectUtils.getProperties()}</code> are:
   *
   * <ol>
   *   <li>The properties provided to this are single-level properties (i.e. "a", "b" means to get properties "a" and
   *        "b" of the provided object), while the properties of that are chained (i.e. "a", "b" means to get property
   *        "a", and then get property "b" of whatever that returns.</li>
   *   <li>This, then, returns multiple values, while that returns a single value</li>
   *   <li>This returns a typed object which is a <code>Partial</code> of the provided object. That returns <code>any</code></li>
   * </ol>
   *
   * So how is this useful? It's nice in tests, where you want to get multiple values for an object and validate them
   * all at once. For example, maybe you've got an array of complex objects and you want to check two values on each
   * of them. You could write two <code>expect<code> statements:
   *
   * <pre>
   *   expect(myArray.map((myThing: MyObject) => myThing.value1)).toEqual([ 1, 19, 37, 54, 12 ]);
   *   expect(myArray.map((myThing: MyObject) => myThing.value2)).toEqual([ "fish", "fowl", "bear", "griffin", "snake" ]);
   * </pre>
   *
   * Or, using this function, you can combine them:
   *
   * <pre>
   *   expect(myArray.map((myThing: MyObject) => ObjectUtils.getPartial(myThing, "value1", "value2"))
   *     .toEqual([
   *       { value1: 1, value2, "fish" },
   *       { value1: 19, value2, "fowl" },
   *       { value1: 37, value2, "bear" },
   *       { value1: 54, value2, "griffin" },
   *       { value1: 12, value2, "snake" },
   *     ]);
   * </pre>
   *
   * Now it's easier to see the correlation between the values being tested. For only two values, it's a wash, but if
   * you've got three or four, it can be very helpful.
   *
   * @param object
   * @param properties
   * @returns {any}
   */
  static getPartial(object, ...properties) {
    const result = {};
    properties.forEach(prop => {
      if (prop in object) {
        result[prop] = object[prop];
      }
    });
    return result;
  }

  /**
   * Iterates over all properties of a simple object and copies from source to target.
   * @param target Target object
   * @param source Source object.
   * @return target (for caller convenience)
   */
  static copyValues(target, source) {
    // sadly IE does not support Object.assign() which might work nicely
    for (const name of Object.keys(source)) {
      target[name] = source[name];
    }
    return target;
  }

  /**
   * Copies field values from the source to the target, like {@link copyValues}, but with the source and target
   * parameters in the correct order.
   *
   * @param source Source object.
   * @param target Target object
   * @return target (for caller convenience)
   */
  static copyFieldValues(source, target) {
    return ObjectUtils.copyValues(target, source);
  }

  /**
   * @deprecated use ObjectUtils.shallowCompare or ObjectUtils.deepCompare instead.
   *
   * Does shallow comparison of 2 Objects' properties.
   * @param {Object} a
   * @param {Object} b
   * @returns {boolean} true if each Object have the same property names and values,
   * false otherwise.
   */
  static areEqual(a, b) {
    let aProps = Object.getOwnPropertyNames(a);
    let bProps = Object.getOwnPropertyNames(b);
    if (aProps.length !== bProps.length) {
      return false;
    }
    for (let aProp of aProps) {
      // @ts-ignore
      if (a[aProp] !== b[aProp]) {
        return false;
      }
    }
    return true;
  }

  /**
   * Implementation of shallowCompare and deepCompare methods.
   * @param a First object to compare
   * @param b Second object to compare
   * @param deepCompare When true, recurse keys in objects.  If shallow, '===' will be used to
   * compare the top-level property values.
   * @param partialCompare Optional. When true, ignore properties of b if they don't exist in a.
   * @returns true if each Object have the same property names and values, false otherwise.
   */
  static compareObjects(a, b, deepCompare, partialCompare) {
    if (a === null || a === undefined || b === null || b === undefined) {
      return a === b;
    }
    // after this just checking type of one would be enough
    if (a.constructor !== b.constructor) {
      return false;
    }
    // if they are functions, they should exactly refer to same one (because of closures)
    if (ClassUtils.isInstanceOf(a, Function)) {
      return a === b;
    }
    // if they are regexps, they should exactly refer to same one (it is hard to better equality check on current ES)
    if (ClassUtils.isInstanceOf(a, RegExp)) {
      return a === b;
    }
    if (a === b || a.valueOf() === b.valueOf()) {
      return true;
    }
    if (partialCompare !== true && Array.isArray(a) && Array.isArray(b) && a.length !== b.length) {
      return false;
    }

    // if they are dates, they must have had equal valueOf
    if (ClassUtils.isInstanceOf(a, Date)) {
      return false;
    }

    // if they are strictly equal then they both need to at least be an object
    if (!ClassUtils.isInstanceOf(a, Object)) {
      return false;
    }
    if (!ClassUtils.isInstanceOf(b, Object)) {
      return false;
    }

    // Special handling for Maps
    if (ClassUtils.isInstanceOf(a, Map) && ClassUtils.isInstanceOf(b, Map)) {
      if (partialCompare !== true && a.size !== b.size) {
        return false;
      }
      return Array.from(a).every(([key, value]) => b.has(key) && deepCompare ? ObjectUtils.deepCompare(value, b.get(key)) : value === b.get(key));
    }

    // recursive object equality check
    const aProps = Object.getOwnPropertyNames(a);
    const bProps = Object.getOwnPropertyNames(b);
    if (partialCompare !== true && aProps.length !== bProps.length) {
      return false;
    }
    if (deepCompare) {
      return aProps.every(function (i) {
        return bProps.indexOf(i) !== -1;
      }) && aProps.filter(x => {
        return !Array.isArray(a) || x !== "length";
      }).every(function (i) {
        return ObjectUtils.compareObjects(a[i], b[i], deepCompare, partialCompare);
      });
    } else {
      return aProps.every(function (propName) {
        return bProps.indexOf(propName) !== -1 && a[propName] === b[propName];
      });
    }
  }

  /**
   * Performs a shallow comparison of two objects.
   * @param a First object to compare
   * @param b Second object to compare
   * @returns true if each Object have the same property names and values, false otherwise.
   */
  static shallowCompare(a, b) {
    return ObjectUtils.compareObjects(a, b, false);
  }

  /**
   * Performs a deep comparison of two objects.
   * @param a First object to compare
   * @param b Second object to compare
   * @returns true if each Object have the same property names and values, false otherwise.
   */
  static deepCompare(a, b) {
    return ObjectUtils.compareObjects(a, b, true);
  }

  /**
   * Performs a deep comparison of two objects, requiring that b contains all the property names and values of a, but
   * not the other way around.
   *
   * @param a First object to compare
   * @param b Second object to compare
   * @returns true if object b has the same property names and values as a, false otherwise.
   *
   * This was written to assist writing unit tests that wanted to easily validate a subset of properties from a more
   * complex object.
   */
  static partialCompare(a, b) {
    return ObjectUtils.compareObjects(a, b, true, true);
  }
  static mergeSorted(...objects) {
    let merged = {};
    for (const object of objects) {
      merged = {
        ...merged,
        ...object
      };
    }
    return Object.keys(merged).sort().reduce((collector, property) => {
      collector[property] = merged[property];
      return collector;
    }, {});
  }

  /**
   * When our minimum browsers support it, it would be ideal to replace any calls to this method with
   * https://developer.mozilla.org/en-US/docs/Web/API/structuredClone.
   */
  static deepCopy(object, replacer) {
    return object != null ? JSON.parse(JSON.stringify(object, replacer)) : object;
  }
  static cement(object, property) {
    Object.defineProperty(object, property, {
      value: object[property]
    });
  }
  static deepFreeze(object) {
    if (object != null && typeof object === "object") {
      Object.values(object).forEach(ObjectUtils.deepFreeze);
    }
    return Object.isFrozen(object) ? object : Object.freeze(object);
  }
  static deepThaw(object) {
    return ObjectUtils.deepCopy(object);
  }
  static createMutable(value) {
    return {
      value
    };
  }
}
_defineProperty(ObjectUtils, "INVALID_PROPERTY_INDICATOR", {});
_defineProperty(ObjectUtils, "OBJECT_METHOD_NAMES", [
// @see lib.d.ts interface Object
"hasOwnProperty", "isPrototypeOf", "propertyIsEnumerable", "toLocaleString", "toString", "valueOf"]);
//# sourceMappingURL=ObjectUtils.js.map
